import { useCallback } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { match } from "ts-pattern";
import { Badge, Card, CardRowDetail, CardSection, Center, HStack, VStack } from "widgets";

import type { ActivityExtendedInstance, ActivityInstanceExtendedParameter } from "@/graphql.ts";
import { FallbackPanel } from "@/modules/workflow/components/ErrorFallbackPanel.tsx";
import type {
  BooleanParameterValueFragment,
  DefaultValuePropsFragment,
  FileParameterValueFragment,
  GenomeParameterValueFragment,
  NumberParameterValueFragment,
  RegionParameterValueFragment,
  SampleParameterValueFragment,
  StringParameterValueFragment,
  TrackerParameterValueFragment,
} from "@/modules/workflow/graphql/workflow.gql.ts";
import { ContentPlaceholder } from "@/ui-modules/core/ContentPlaceholder.tsx";

export interface PreviewParameterRendererProps {
  selectedActivity: ActivityExtendedInstance | undefined;
}

export const PreviewParameterRenderer = (props: PreviewParameterRendererProps) => {
  const getParameterLabel = useCallback(
    (parameterId: string) =>
      props.selectedActivity?.parameters.find((p) => p.id === parameterId)?.parameterDefinition.label,
    [props.selectedActivity],
  );

  if (!props.selectedActivity) {
    return (
      <Card noBorder fillHeight>
        <Center height="100%">
          <ContentPlaceholder mini>Select activity to show parameters</ContentPlaceholder>
        </Center>
      </Card>
    );
  }

  return (
    <ErrorBoundary FallbackComponent={FallbackPanel}>
      <Card scrollable noBorder>
        <CardSection title={props.selectedActivity?.label} />
        {props.selectedActivity?.parameters?.map((parameter) => {
          const value = getParameterValue(parameter);
          return (
            <CardRowDetail
              title={
                <HStack>
                  {getParameterLabel(parameter.id)}
                  {parameter.staged ? (
                    <Center height={18} width={18}>
                      <Badge label="P" inline />
                    </Center>
                  ) : null}
                </HStack>
              }
              key={parameter.id}
            >
              <HStack>
                <VStack spacing={1}>{value?.map((v, idx) => <span key={`${v}-${idx}`}>{v}</span>)}</VStack>
              </HStack>
            </CardRowDetail>
          );
        })}
      </Card>
    </ErrorBoundary>
  );
};

function getParameterValue(parameter: ActivityInstanceExtendedParameter): string[] {
  return match(parameter.values as DefaultValuePropsFragment)
    .with({ __typename: "BooleanValue" }, () => {
      return [String((parameter.values as BooleanParameterValueFragment)?.booleanValue)];
    })
    .with({ __typename: "StringValue" }, () => {
      return (parameter.values as StringParameterValueFragment)?.stringValue;
    })
    .with({ __typename: "NumberValue" }, () => {
      return (parameter.values as NumberParameterValueFragment)?.numberValue;
    })
    .with({ __typename: "TrackerValue" }, () => {
      const trackerValue = (parameter.values as TrackerParameterValueFragment).trackerValue;
      return [`${trackerValue.attributeName} (${trackerValue.attributeType.name})`];
    })
    .with({ __typename: "SampleValue" }, () => {
      const sampleValue = (parameter.values as SampleParameterValueFragment).sampleValue;
      return sampleValue.map((p) => {
        const isHidden = p?.sample?.hidden;
        const isDeleted = p?.sample?.deleted;

        if (isDeleted) {
          return "Deleted Sample";
        } else if (isHidden) {
          return "Restricted Sample";
        } else {
          return p?.sample?.name ?? "-";
        }
      });
    })
    .with({ __typename: "RegionValue" }, () => {
      const regionValue = (parameter.values as RegionParameterValueFragment)?.regionValue;
      return [
        `${regionValue?.contig?.name ?? regionValue?.contig?.accession} : ${regionValue?.range?.start} - ${regionValue?.range?.stop}`,
      ];
    })
    .with({ __typename: "FileValue" }, () => {
      const fileValue = (parameter.values as FileParameterValueFragment)?.fileValue;
      return fileValue.map((file) => {
        const isHidden = file?.hidden;
        const isDeleted = file?.deleted;

        if (isDeleted) {
          return "Deleted File";
        } else if (isHidden) {
          return "Restricted File";
        } else {
          return file?.fileName ?? "-";
        }
      });
    })
    .with({ __typename: "GenomeValue" }, () => {
      const value = (parameter.values as GenomeParameterValueFragment)?.genomeValue;
      return value?.hidden
        ? ["[Restricted Genome]"]
        : [(parameter.values as GenomeParameterValueFragment)?.genomeValue.name];
    })
    .otherwise(() => ["-"]) as string[];
}
